/**
 * Content hashing for report identity.
 *
 * Used only to recognise the same document arriving twice, so a fast
 * non-cryptographic hash is the right tool. Keys are sorted before hashing so
 * that two documents differing only in key order hash alike.
 */
export function contentHash(value: unknown): string {
  const text = canonicalise(value)
  // FNV-1a, run as two interleaved lanes to widen the output and keep
  // collisions unlikely across a few thousand documents.
  let a = 0x811c9dc5
  let b = 0x01000193
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    a ^= code
    a = Math.imul(a, 0x01000193) >>> 0
    b ^= code + i
    b = Math.imul(b, 0x85ebca6b) >>> 0
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0')
}

function canonicalise(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalise).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>).sort(([x], [y]) =>
    x < y ? -1 : x > y ? 1 : 0,
  )
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalise(v)}`).join(',')}}`
}
